import React, { useContext } from 'react'
import { FormattedMessage, useIntl, Link } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import ChatIcon from 'src/assets/icons/icon_chat.svg'
import {
  Bold,
  Description,
  Title
} from 'src/features/stocks/components/typography'
import Documents from 'src/features/stocks/components/OfferDetails/Documents'
import { isEmpty, not } from 'ramda'
import Alert from 'src/components/Alert'
import SelectUser from 'src/components/SelectUser'
import PermissionsContext, {
  hasPermissionTo,
  PERMISSIONS
} from 'src/utils/permissions'
import { Avatar } from 'src/components/atoms/Avatar'
import routes from 'src/utils/routes'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'
import { STOCKS_TYPES } from 'src/features/stocks/ducks/consts'
import TopContainerCertificate from 'src/assets/top_container_certificate.svg'
import IconWithTooltip from 'src/components/IconWithTooltip'

const Contact = ({
  className,
  stockType,
  handler,
  changeHandler,
  offerBelongsToCurrentUser,
  offerBelongsToCurrentUserCompany,
  isUpdatable,
  handleConversationStart = () => {},
  bottomSlot = null,
  issuer: {
    company: {
      name,
      address,
      city,
      countryCode,
      documents = [],
      postalCode,
      id,
      isTopContainerCertified
    }
  }
}) => {
  const intl = useIntl()
  const permissions = useContext(PermissionsContext)
  const showDocuments = not(isEmpty(documents))
  const requiredLicenceType = [
    STOCKS_TYPES.FREE_CARRIERS,
    STOCKS_TYPES.POST_IMPORT
  ].includes(stockType)
    ? 'carrier_license'
    : 'forwarder_license'

  const hasRequiredLicence =
    Array.isArray(documents) &&
    documents.length > 0 &&
    documents.some(doc => doc.type === requiredLicenceType)

  const showLicenceWarningMessage =
    Array.isArray(documents) && documents.length > 0 && !hasRequiredLicence
  const showChangeUserSelect =
    (isUpdatable &&
      offerBelongsToCurrentUser &&
      hasPermissionTo(PERMISSIONS.OFFER_EDIT_OWN, permissions)) ||
    (isUpdatable &&
      offerBelongsToCurrentUserCompany &&
      hasPermissionTo(PERMISSIONS.OFFER_EDIT_ANY, permissions))

  const userCantUndoChange =
    offerBelongsToCurrentUser &&
    !hasPermissionTo(PERMISSIONS.OFFER_EDIT_ANY, permissions)

  return (
    <div className={className}>
      <div>
        <Title>
          <FormattedMessage id='offerDetails.contactPerson' />
        </Title>
        {!showChangeUserSelect && (
          <User>
            <Avatar url={handler.avatar && handler.avatar.url} />
            <Username>
              <Bold>{`${handler.firstName} ${handler.lastName}`}</Bold>
              <Description>
                <a href={`tel:${handler.phoneNumber}`}>{handler.phoneNumber}</a>
              </Description>
            </Username>
            {!offerBelongsToCurrentUser && (
              <ChatIconContainer>
                <ChatIcon onClick={() => handleConversationStart()} />
              </ChatIconContainer>
            )}
          </User>
        )}
        {showChangeUserSelect && (
          <SelectUser
            handler={handler}
            onChange={changeHandler}
            userCantUndoChange={userCantUndoChange}
          />
        )}
      </div>

      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }}>
          <Title>
            <FormattedMessage id='offerDetails.contactCompany' />
          </Title>
          <Company>
            <Bold>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.COMPANY}&companyId=${id}`}
              >
                {name}
              </Link>
            </Bold>
          </Company>
          {showLicenceWarningMessage && (
            <StyledAlert
              type='warning'
              message={intl.formatMessage({
                id: `offerDetails.no${
                  [
                    STOCKS_TYPES.FREE_CARRIERS,
                    STOCKS_TYPES.POST_IMPORT
                  ].includes(stockType)
                    ? 'Carrier'
                    : 'Forwarder'
                }LicenceWarning`
              })}
            />
          )}
          <Description as='div'>
            <p>{address}</p>
            <p>
              {`${postalCode} ${city}${countryCode ? `, ${countryCode}` : ''}`}
            </p>
          </Description>
        </div>
        {isTopContainerCertified && (
          <div
            style={{
              flex: '0 0 auto',
              'max-width': '100%',
              'max-height': '100%',
              display: 'flex',
              'vertical-align': 'middle'
            }}
          >
            <IconWithTooltip
              title={intl.formatMessage({
                id: 'common.companyTopContainerCertified'
              })}
              icon={
                <img
                  style={{ 'margin-left': '10px' }}
                  src={TopContainerCertificate}
                  width={80}
                  alt='Top Container Certificate'
                />
              }
            />
          </div>
        )}
      </div>

      {showDocuments && <Documents documents={documents} />}

      {bottomSlot}
    </div>
  )
}

const Company = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  a:hover {
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
  }
`

const Username = styled.div`
  margin-left: 1rem;
`

const ChatIconContainer = styled.span`
  margin-left: auto;
  display: block;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.7).hex()};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
  }
`

const User = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 12px;
`

const StyledAlert = styled(Alert)`
  margin: 0.5rem 0;
`

export default styled(Contact)`
  background-color: ${({ theme }) => theme.colors.alabaster.hex()};
  flex: 1;
  && {
    padding: 0;
  }
  & > div {
    padding: 0.75rem 1rem 0.75rem 1rem;
  }
`
